import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { LICENSE_DEFINITIONS } from '@components/shared/license/license.constants';
import {
    ClientErrorCodes,
    FeatureFlag,
    ILicense,
    ILicenseOfflineRequest,
    ILicenseRequest,
    LicenseType,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    ActivateOfflineModalComponent,
    ActivateOnlineModalComponent,
    LicenseUpdateType,
    UpdateModalComponent,
} from '@pages/license/modals';
import { DeactivateOfflineModalComponent } from '@pages/license/modals/deactivate-offline';
import {
    FileUploadService,
    ModalService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { unsubscribe } from '@utils/object.utils';

import {
    merge,
    Subscription,
} from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'ui-license-dashboard',
    templateUrl: './ui-license-dashboard.component.html',
    styleUrls: ['./ui-license-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiLicenseDashboardComponent implements OnDestroy {
    @Input()
    public license?: ILicense;

    @Input()
    public editPermission: string[] = [];

    @Input()
    public deletePermission: string[] = [];

    @Input()
    public isHost = false;

    // Event is fired when a license file is uploaded
    @Output()
    public upload = new EventEmitter<ILicenseOfflineRequest>();

    // Event is fired when an online activation occurs
    @Output()
    public online = new EventEmitter<ILicenseRequest>();

    // Event is fired when an offline activation request occurs
    @Output()
    public offline = new EventEmitter<ILicenseRequest>();

    // Event is fired when a license is deactivated
    @Output()
    public deactivated = new EventEmitter<number | undefined>();

    // Event is fired when a license is updated
    @Output()
    public update = new EventEmitter<void>();

    // Event is fired when a license is deleted
    @Output()
    public remove = new EventEmitter<{ deactivate: boolean, hostLicenseId?: number }>();

    // Event is fired when a license is allocated to tenants
    @Output()
    public allocate = new EventEmitter<void>();

    public isLoading?: boolean;

    public licenseDefinitions = LICENSE_DEFINITIONS;
    public activationRequest!: string;

    public isLegacyMode = this._appState.settings.isFeatureEnabled(FeatureFlag.LicenseLegacyMode);
    public serverInstallationId = this._appState.settings.get(Setting.installationId);
    public serverActivationId$ = this._appState.settings.get$(Setting.activationId);

    private _removeLicenseSub?: Subscription;

    constructor(
        private _fileUploadService: FileUploadService,
        private _translateService: TranslateService,
        private _appState: AppStateFacade,
        private _modalService: ModalService,
        private _snack: UiSnackBarService,
    ) { }

    ngOnDestroy() {
        unsubscribe(
            this._removeLicenseSub,
        );
    }

    public onSubmit(files: File[]) {
        const file = files[0];

        if (!this._fileUploadService.isFileItemValid(file, null, 102400)) {
            if (file) {
                this._snack.error(
                    `${this._translateService.instant('settings_license_form_upload_invalid_warning')} (#${
                    ClientErrorCodes.InvalidLicenseFormat
                    })`,
                );
            }
            return;
        }

        this.upload.emit({ file, type: LicenseType.Legacy });
    }

    public removeLicense(event: Event) {
        event.preventDefault();
        this._modalService.onConfirm(
            null,
            () => this.remove.emit({ deactivate: true, hostLicenseId: this.license!.hostLicenseId }),
            () => { },
            this._translateService.instant('license_action_remove_confirmation'),
        );
    }

    public openActivateOnlineModal(offline: boolean = false) {
        this._modalService
            .openModal(ActivateOnlineModalComponent,
                {
                    installationId: this.serverInstallationId,
                    offline,
                } as ILicenseRequest)
            .afterClosedResult()
            .subscribe(result => {
                if (result.type === ModalResultType.Add) {
                    this.online.emit(result.data);
                } else if (result.type === ModalResultType.Failed) {
                    this.openActivateOfflineModal();
                }
            });
    }

    public openActivateOfflineModal(update: boolean = false) {
        const modalRef = this._modalService
            .openModal(ActivateOfflineModalComponent,
                {
                    installationId: this.serverInstallationId,
                    update,
                } as ILicenseRequest);
        merge(
            modalRef.customEvent,
            modalRef.afterClosedResult(),
        )
        .pipe(
            take(2),
        )
        .subscribe(result => {
                if (result.type === ModalResultType.Generic) {
                    this.offline.emit(result.data);
                }
                if (result.type === ModalResultType.Add) {
                    this.upload.emit(result.additionalData);
                }
            });
    }

    public openUpdateModal(failed: boolean = false) {
        this._modalService
            .openModal(UpdateModalComponent, failed)
            .afterClosedResult()
            .pipe(
                take(1),
            )
            .subscribe(result => {
                if (result.type === ModalResultType.Update) {
                    if (result.additionalData === LicenseUpdateType.Offline) {
                        this.openActivateOfflineModal(true);
                    } else if (result.additionalData === LicenseUpdateType.Online) {
                        this.update.emit();
                    }
                }
            });
    }

    public openDeactivateOfflineModal(tenantId?: number) {
        const modal$ = this._modalService
            .openModal(DeactivateOfflineModalComponent, tenantId);
        merge(
            modal$.customEvent,
            modal$.afterClosedResult(),
        )
        .pipe(
            take(2),
        )
        .subscribe(result => {
            if (result.type === ModalResultType.Generic) {
                this.deactivated.emit(result.data);
            } else if (result.type === ModalResultType.Failed) {
                this.remove.emit({ deactivate: false, hostLicenseId: this.license!.hostLicenseId });
            }
        });
    }
}
